/* eslint-disable react/no-unstable-nested-components */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Building2, Mail, Phone, Pencil, User, MapPin, Briefcase, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextEditor from '@/components/RichTextEditor';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const JOB_TITLES = [
  'Building Inspector', 'Building Permits Examiner', 'Clerk', 'Design Engineer',
  'Environmental Analyst', 'Fire Plan Inspector', 'Gas Inspector', 'Inspector',
  'Plan Examiner', 'Plumbing Inspector', 'ZBA',
];

const DEFAULT_JURISDICTIONS = [
  // Retained non-town legacy entries (NYC boroughs, agencies, utilities)
  'Brooklyn', 'City of Yonkers', 'FDNY',
  'Manhattan TOPO', 'PSEG', 'Queens',
  'Village of East Hill', 'Village of Flower Hill',
  // Long Island municipalities
  'City of Glen Cove', 'City of Long Beach',
  'Town of Babylon', 'Town of Brookhaven', 'Town of East Hampton',
  'Town of Hempstead', 'Town of Huntington', 'Town of Islip',
  'Town of North Hempstead', 'Town of Oyster Bay', 'Town of Riverhead',
  'Town of Shelter Island', 'Town of Smithtown', 'Town of Southampton',
  'Town of Southold',
  'Village of Amityville', 'Village of Asharoken', 'Village of Atlantic Beach',
  'Village of Babylon', 'Village of Belle Terre', 'Village of Bellerose',
  'Village of Bellport', 'Village of Brightwaters', 'Village of Brookville',
  'Village of Cedarhurst', 'Village of Centre Island', 'Village of Cove Neck',
  'Village of Dering Harbor', 'Village of East Hampton', 'Village of East Hills',
  'Village of East Rockaway', 'Village of East Williston',
  'Village of Farmingdale', 'Village of Floral Park', 'Village of Freeport',
  'Village of Garden City', 'Village of Great Neck',
  'Village of Great Neck Estates', 'Village of Great Neck Plaza',
  'Village of Greenport', 'Village of Hewlett Bay Park',
  'Village of Hewlett Harbor', 'Village of Hewlett Neck',
  'Village of Huntington Bay', 'Village of Island Park',
  'Village of Islandia', 'Village of Kensington', 'Village of Kings Point',
  'Village of Lake Grove', 'Village of Lake Success',
  'Village of Lattingtown', 'Village of Laurel Hollow',
  'Village of Lawrence', 'Village of Lindenhurst', 'Village of Lloyd Harbor',
  'Village of Lynbrook', 'Village of Malverne', 'Village of Manorhaven',
  'Village of Massapequa Park', 'Village of Matinecock',
  'Village of Mill Neck', 'Village of Muttontown', 'Village of New Hyde Park',
  'Village of Nissequogue', 'Village of North Haven', 'Village of North Hills',
  'Village of Northport', 'Village of Ocean Beach', 'Village of Old Field',
  'Village of Old Westbury', 'Village of Oyster Bay Cove',
  'Village of Patchogue', 'Village of Plandome',
  'Village of Plandome Heights', 'Village of Plandome Manor',
  'Village of Poquott', 'Village of Port Jefferson',
  'Village of Port Washington North', 'Village of Quogue',
  'Village of Rockville Centre', 'Village of Roslyn',
  'Village of Roslyn Estates', 'Village of Roslyn Harbor',
  'Village of Russell Gardens', 'Village of Saddle Rock',
  'Village of Sag Harbor', 'Village of Sagaponack', 'Village of Saltaire',
  'Village of Sands Point', 'Village of Sea Cliff', 'Village of Shoreham',
  'Village of South Floral Park', 'Village of Southampton',
  'Village of Stewart Manor', 'Village of Thomaston',
  'Village of Upper Brookville', 'Village of Valley Stream',
  'Village of the Branch', 'Village of West Hampton Dunes',
  'Village of Westhampton Beach', 'Village of Woodsburgh',
];

const CLIENT_TYPES = ['Residential', 'Commercial'];

// ─── Clients Tab ───
function ClientsTab({ clients, contacts, onRefresh }) {
  const { hasPerm } = useAuth();
  const canManage = !!hasPerm('manage_clients');
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [form, setForm] = useState({});

  const openCreate = () => {
    setEditClient(null);
    setClientDetail(null);
    setForm({ name: '', email: '', phone: '', address: '', client_type: '', primary_contact_id: '', billable_contact_id: '' });
    setSheetOpen(true);
  };

  const openEdit = async (c) => {
    setEditClient(c);
    setForm({
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      address: c.address || '', client_type: c.client_type || '',
      primary_contact_id: c.primary_contact_id || '',
      billable_contact_id: c.billable_contact_id || '',
    });
    // Fetch full detail with enriched contact info
    try {
      const r = await api.get(`/clients/${c.id}`);
      setClientDetail(r.data);
    } catch { setClientDetail(null); }
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Client name required');
    const payload = {
      ...form,
      primary_contact_id: form.primary_contact_id || null,
      billable_contact_id: form.billable_contact_id || null,
    };
    try {
      if (editClient) { await api.put(`/clients/${editClient.id}`, payload); toast.success('Client updated'); }
      else { await api.post('/clients', payload); toast.success('Client created'); }
      setSheetOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteClient = async () => {
    if (!editClient || !window.confirm(`Delete client "${editClient.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${editClient.id}`);
      toast.success('Client deleted');
      setSheetOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting client'); }
  };

  const filtered = clients.filter(c => (c.name || '').toLowerCase().includes(search.toLowerCase()));

  const ContactInfoBlock = ({ label, detail }) => {
    if (!detail || !detail.name) return null;
    return (
      <div className="p-3 rounded-md border border-border bg-background">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{label}</p>
        <p className="text-sm font-medium">{detail.name}</p>
        {detail.job_title && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Briefcase className="h-3 w-3" /> {detail.job_title}</p>}
        {detail.email && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" /> {detail.email}</p>}
        {detail.phone && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {detail.phone}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="clients-search" />
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground ml-3" data-testid="create-client-button" disabled={!canManage} title={canManage ? '' : 'You don\'t have permission to create clients'}>
          <Plus className="h-4 w-4 mr-1" /> New Client
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
        {filtered.map(c => (
          <Card key={c.id} className="border-border bg-card shadow-none hover:bg-secondary/30 transition-colors cursor-pointer group" onClick={() => openEdit(c)} data-testid={`client-card-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center text-primary"><Building2 className="h-5 w-5" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{c.name}</p>
                      {c.client_type && <Badge variant="outline" className="text-xs">{c.client_type}</Badge>}
                    </div>
                    {c.email && <span className="flex items-center gap-1 text-xs text-muted-foreground mt-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5"><Phone className="h-3 w-3" /> {c.phone}</span>}
                    {c.primary_contact_name && <span className="text-xs text-muted-foreground mt-0.5 block">Primary: {c.primary_contact_name}</span>}
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"><Pencil className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground col-span-3 text-center py-8">No clients found</p>}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">{editClient ? 'Edit Client' : 'New Client'}</SheetTitle><SheetDescription>Client information</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Company Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="client-form-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Client Type</Label>
              <SearchableSelect
                value={form.client_type || ''}
                onValueChange={v => setForm({ ...form, client_type: v })}
                options={[{ value: '', label: '(None)' }, ...CLIENT_TYPES.map(t => ({ value: t, label: t }))]}
                placeholder="Select type"
                searchPlaceholder="Search types..."
                data-testid="client-form-type"
              />
            </div>
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Address</Label><ExpandableTextarea value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-background border-border" rows={2} expandedRows={5} /></div>

            <div>
              <Label className="text-xs text-muted-foreground">Early-Pay Discount %</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="99.99"
                placeholder="e.g. 2"
                value={form.early_pay_discount_percent ?? ''}
                onChange={e => setForm({ ...form, early_pay_discount_percent: e.target.value === '' ? null : parseFloat(e.target.value) })}
                className="bg-background border-border"
                data-testid="client-form-early-pay-discount"
              />
              <p className="text-xs text-muted-foreground mt-1">Default discount offered to this client when they pay early. Leave blank if no agreement exists.</p>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Primary Contact</Label>
              <SearchableSelect
                value={form.primary_contact_id || ''}
                onValueChange={v => setForm({ ...form, primary_contact_id: v })}
                options={[{ value: '', label: '(None)' }, ...contacts.map(ct => ({ value: ct.id, label: `${ct.name}${ct.job_title ? ` (${ct.job_title})` : ''}` }))]}
                placeholder="Select primary contact"
                searchPlaceholder="Search contacts..."
                data-testid="client-form-primary-contact"
              />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Billable Contact</Label>
              <SearchableSelect
                value={form.billable_contact_id || ''}
                onValueChange={v => setForm({ ...form, billable_contact_id: v })}
                options={[{ value: '', label: '(None)' }, ...contacts.map(ct => ({ value: ct.id, label: `${ct.name}${ct.job_title ? ` (${ct.job_title})` : ''}` }))]}
                placeholder="Select billable contact"
                searchPlaceholder="Search contacts..."
                data-testid="client-form-billable-contact"
              />
            </div>

            {/* Inline Contact Detail Display */}
            {editClient && clientDetail && (
              <div className="space-y-2" data-testid="client-contact-details">
                <ContactInfoBlock label="Primary Contact" detail={clientDetail.primary_contact_detail} />
                <ContactInfoBlock label="Billable Contact" detail={clientDetail.billable_contact_detail} />
              </div>
            )}

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="client-form-submit" disabled={!canManage} title={canManage ? '' : 'You don\'t have permission to manage clients'}>{editClient ? 'Update Client' : 'Create Client'}</Button>
            {editClient && canManage && (
              <Button variant="outline" onClick={handleDeleteClient} className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 gap-1.5" data-testid="client-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete Client
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Contacts Tab ───
function ContactsTab({ contacts, clients, onRefresh, townOnly }) {
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState({});
  const [jurisdictions, setJurisdictions] = useState([]);
  const [clientSearch, setClientSearch] = useState('');

  const fetchJurisdictions = useCallback(async () => {
    try {
      const r = await api.get('/jurisdictions');
      setJurisdictions(r.data || []);
    } catch { /* fall back to legacy hardcoded list silently */ }
  }, []);

  useEffect(() => { fetchJurisdictions(); }, [fetchJurisdictions]);

  const displayed = townOnly
    ? contacts.filter(c => c.jurisdiction && c.jurisdiction.trim() !== '')
    : contacts;

  // Merge backend catalog + the hardcoded defaults (back-compat shim for
  // tabs that loaded before the API call resolves) + any jurisdiction value
  // already on a contact (so legacy data renders without surprises).
  const catalogNames = jurisdictions.map(j => j.name);
  const existingJurisdictions = contacts.map(c => c.jurisdiction).filter(j => j && j.trim());
  const allJurisdictions = [...new Set([
    ...catalogNames,
    ...DEFAULT_JURISDICTIONS,
    ...existingJurisdictions,
  ])].sort();

  const filtered = displayed.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.job_title || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.jurisdiction || '').toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditContact(null);
    setForm({ name: '', email: '', phone: '', job_title: '', jurisdiction: '', comments: '', linked_clients: [] });
    setSheetOpen(true);
  };
  const openEdit = (c) => { setEditContact(c); setForm({ ...c, linked_clients: c.linked_clients || [] }); setSheetOpen(true); };

  const handleSave = async () => {
    if (!form.name?.trim()) return toast.error('Contact name is required');
    try {
      if (editContact) { await api.put(`/contacts/${editContact.id}`, form); toast.success('Contact updated'); }
      else { await api.post('/contacts', form); toast.success('Contact created'); }
      setSheetOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async () => {
    if (!editContact || !window.confirm(`Delete contact "${editContact.name}"?`)) return;
    try {
      await api.delete(`/contacts/${editContact.id}`);
      toast.success('Contact deleted');
      setSheetOpen(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting contact'); }
  };

  const toggleLinkedClient = (clientId) => {
    const current = form.linked_clients || [];
    setForm({
      ...form,
      linked_clients: current.includes(clientId)
        ? current.filter(id => id !== clientId)
        : [...current, clientId],
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="contacts-search" />
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground ml-3" data-testid="create-contact-button">
          <Plus className="h-4 w-4 mr-1" /> New Contact
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contacts-grid">
        {filtered.map(c => (
          <Card key={c.id} className="border-border bg-card shadow-none hover:bg-secondary/30 transition-colors cursor-pointer group" onClick={() => openEdit(c)} data-testid={`contact-card-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-cyan-500/15 flex items-center justify-center text-cyan-400"><User className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{c.name}</p>
                    {c.job_title && <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5"><Briefcase className="h-3 w-3 shrink-0" /> {c.job_title}</p>}
                    {c.jurisdiction && <Badge variant="outline" className="text-xs mt-1 gap-0.5"><MapPin className="h-2.5 w-2.5" /> {c.jurisdiction}</Badge>}
                    <div className="flex items-center gap-3 mt-1">
                      {c.email && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {c.email}</span>}
                      {c.phone && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" /> {c.phone}</span>}
                    </div>
                    {(c.linked_client_names || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {c.linked_client_names.map((name, i) => (
                          <Badge key={i} className="text-xs bg-primary/10 text-primary border-primary/20">{name}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"><Pencil className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground col-span-3 text-center py-8">{townOnly ? 'No town contacts found' : 'No contacts found'}</p>}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">{editContact ? 'Edit Contact' : 'New Contact'}</SheetTitle>
            <SheetDescription>Contact information</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Contact Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="contact-form-name" /></div>
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" data-testid="contact-form-email" /></div>
            <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" data-testid="contact-form-phone" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Job Title</Label>
              <SearchableSelect
                value={form.job_title || ''}
                onValueChange={v => setForm({ ...form, job_title: v })}
                options={[{ value: '', label: '(None)' }, ...JOB_TITLES.map(j => ({ value: j, label: j }))]}
                placeholder="Select job title"
                searchPlaceholder="Search job titles..."
                data-testid="contact-form-job-title"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Jurisdiction</Label>
              <SearchableSelect
                value={form.jurisdiction || ''}
                onValueChange={v => setForm({ ...form, jurisdiction: v })}
                options={[{ value: '', label: '(None)' }, ...allJurisdictions.map(j => ({ value: j, label: j }))]}
                placeholder="Select jurisdiction"
                searchPlaceholder="Search or type to add new..."
                allowCustom
                onAddCustom={async (v) => {
                  // Optimistic add to in-memory list so the dropdown updates immediately,
                  // then persist to the catalog so it's available app-wide moving forward.
                  setJurisdictions(prev => prev.some(j => j.name === v) ? prev : [...prev, { name: v, _optimistic: true }]);
                  try {
                    await api.post('/jurisdictions', { name: v });
                    fetchJurisdictions();
                    toast.success(`"${v}" added to jurisdictions`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to save jurisdiction (kept locally for this contact)');
                  }
                }}
                data-testid="contact-form-jurisdiction"
              />
            </div>
            <div><Label className="text-xs text-muted-foreground">Comments</Label><RichTextEditor value={form.comments || ''} onChange={v => setForm({ ...form, comments: v })} placeholder="Comments..." collapsedHeight={80} data-testid="contact-form-comments" /></div>

            {/* Linked Clients */}
            <div>
              <Label className="text-xs text-muted-foreground">Linked Clients</Label>
              <Input
                placeholder="Search clients..."
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                className="bg-background border-border mt-1 h-8 text-xs"
                data-testid="contact-client-search"
              />
              <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto border border-border rounded-md p-2 bg-background">
                {clients.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No clients available</p>
                ) : clients.filter(cl => cl.name.toLowerCase().includes(clientSearch.toLowerCase())).map(cl => (
                  <label key={cl.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-secondary/30 cursor-pointer text-sm" data-testid={`link-client-${cl.id}`}>
                    <input
                      type="checkbox"
                      checked={(form.linked_clients || []).includes(cl.id)}
                      onChange={() => toggleLinkedClient(cl.id)}
                      className="rounded border-border"
                    />
                    {cl.name}
                  </label>
                ))}
              </div>
              {(form.linked_clients || []).length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{form.linked_clients.length} client(s) linked</p>
              )}
            </div>

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="contact-form-submit">{editContact ? 'Update Contact' : 'Create Contact'}</Button>
            {editContact && (
              <Button variant="outline" onClick={handleDelete} className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 gap-1.5" data-testid="contact-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete Contact
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Main Page ───
export default function ClientsPage() {
  const [pageTab, setPageTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);

  const fetchClients = useCallback(() => api.get('/clients').then(r => setClients(r.data)).catch(() => {}), []);
  const fetchContacts = useCallback(() => api.get('/contacts').then(r => setContacts(r.data)).catch(() => {}), []);

  useEffect(() => { fetchClients(); fetchContacts(); }, [fetchClients, fetchContacts]);

  const townContactCount = contacts.filter(c => c.jurisdiction && c.jurisdiction.trim() !== '').length;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="clients-page">
      <div>
        <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Clients & Contacts<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{clients.length} clients · {contacts.length} contacts</span></h1>
      </div>

      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList data-testid="clients-page-tabs">
          <TabsTrigger value="clients" className="text-xs gap-1" data-testid="tab-clients">
            <Building2 className="h-3 w-3" /> Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="all-contacts" className="text-xs gap-1" data-testid="tab-all-contacts">
            <User className="h-3 w-3" /> All Contacts ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="town-contacts" className="text-xs gap-1" data-testid="tab-town-contacts">
            <MapPin className="h-3 w-3" /> Town Contacts ({townContactCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-3">
          <ClientsTab clients={clients} contacts={contacts} onRefresh={fetchClients} />
        </TabsContent>

        <TabsContent value="all-contacts" className="mt-3">
          <ContactsTab contacts={contacts} clients={clients} onRefresh={fetchContacts} townOnly={false} />
        </TabsContent>

        <TabsContent value="town-contacts" className="mt-3">
          <ContactsTab contacts={contacts} clients={clients} onRefresh={fetchContacts} townOnly={true} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
